import { BrPopoverContainerComponent } from './popover-container.component';
import { Dialog, DialogRef } from '@angular/cdk/dialog';
import { Overlay } from '@angular/cdk/overlay';
import { ESCAPE, hasModifierKey } from '@angular/cdk/keycodes';
import { fromEvent, takeUntil } from 'rxjs';
import { Directive, ElementRef, EventEmitter, inject, Input, NgZone, OnDestroy, Output, TemplateRef, ViewContainerRef } from '@angular/core';

/** Anchored form dialog. CDK owns the overlay, focus trap and dismissal. */
@Directive({
  selector: 'button[brPopoverFor]',
  standalone: true,
  exportAs: 'brPopoverTrigger',
  host: {
    'type': 'button',
    '[attr.aria-haspopup]': 'brPopoverFor ? "dialog" : null',
    '[attr.aria-expanded]': 'isOpen()',
    '[attr.aria-controls]': 'panelId',
    '(click)': 'toggle()',
  },
})
export class BrPopoverTriggerDirective implements OnDestroy {
  private readonly dialog = inject(Dialog);
  private readonly zone = inject(NgZone);
  private readonly overlay = inject(Overlay);
  private readonly element = inject<ElementRef<HTMLButtonElement>>(ElementRef);
  private readonly viewContainerRef = inject(ViewContainerRef);
  private ref?: DialogRef<unknown>;

  @Input({ required: true }) brPopoverFor: TemplateRef<unknown> | null = null;
  @Input({ required: true }) brPopoverLabel = '';
  @Output() readonly popoverOpened = new EventEmitter<void>();
  @Output() readonly popoverClosed = new EventEmitter<void>();

  get panelId(): string | null { return this.ref?.id ?? null; }
  isOpen(): boolean { return !!this.ref; }

  open(): void {
    const template = this.brPopoverFor;
    if (!template || this.element.nativeElement.disabled || this.ref) return;
    // Programmatic callers may be outside Angular; rendering and focus must
    // participate in its normal change-detection/stability cycle.
    this.zone.run(() => {
      const position = this.overlay.position().flexibleConnectedTo(this.element)
        .withViewportMargin(8).withGrowAfterOpen().withPositions([
          { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top' },
          { originX: 'end', originY: 'bottom', overlayX: 'end', overlayY: 'top' },
          { originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom' },
          { originX: 'end', originY: 'top', overlayX: 'end', overlayY: 'bottom' },
        ]);
      this.ref = this.dialog.open(template, {
        viewContainerRef: this.viewContainerRef,
        positionStrategy: position,
        scrollStrategy: this.overlay.scrollStrategies.reposition(),
        container: BrPopoverContainerComponent,
        backdropClass: 'cdk-overlay-transparent-backdrop',
        ariaLabel: this.brPopoverLabel,
        ariaModal: true,
        autoFocus: 'first-tabbable',
        restoreFocus: this.element.nativeElement,
        templateContext: { popover: this },
      });
      const ref = this.ref;
      // Consume Escape at the pane boundary before an enclosing application handler.
      // DialogRef still owns disposal and focus restoration.
      fromEvent<KeyboardEvent>(ref.overlayRef.overlayElement, 'keydown')
        .pipe(takeUntil(ref.closed)).subscribe(event => {
          if (event.keyCode === ESCAPE && !hasModifierKey(event)) {
            event.preventDefault();
            event.stopPropagation();
            ref.close();
          }
        });
      this.ref.closed.subscribe(() => {
        this.ref = undefined;
        this.popoverClosed.emit();
      });
      this.popoverOpened.emit();
    });
  }

  close(): void { this.zone.run(() => this.ref?.close()); }
  toggle(): void { if (this.isOpen()) this.close(); else this.open(); }

  ngOnDestroy(): void {
    this.close();
    this.popoverOpened.complete();
    this.popoverClosed.complete();
  }
}
